import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Link, useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { CalendarDays, Heart, Star, X, CreditCard, CalendarClock } from "lucide-react";
import TrainerCard from "@/components/TrainerCard";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter
} from "@/components/ui/dialog";

const STATUS_COLOR = {
  pending: "bg-amber-100 text-amber-800",
  confirmed: "bg-emerald-100 text-emerald-800",
  completed: "bg-neutral-900 text-white",
  declined: "bg-red-100 text-red-800",
  cancelled: "bg-neutral-200 text-neutral-700",
};

export default function CustomerDashboard() {
  const [params, setParams] = useSearchParams();
  const tab = params.get("tab") || "bookings";
  const qc = useQueryClient();

  const bookings = useQuery({ queryKey: ["cust-bookings"], queryFn: async () => (await api.get("/customer/bookings")).data });
  const favs = useQuery({ queryKey: ["favs"], queryFn: async () => (await api.get("/favourites")).data });

  const [reviewFor, setReviewFor] = useState(null);
  const [rating, setRating] = useState(5);
  const [comment, setComment] = useState("");

  const cancelMut = useMutation({
    mutationFn: ({ id, reason }) => api.post(`/customer/bookings/${id}/cancel`, { reason: reason || "" }),
    onSuccess: () => { toast.success("Booking cancelled"); setCancelFor(null); setCancelReason(""); qc.invalidateQueries({ queryKey: ["cust-bookings"] }); },
  });
  const [cancelFor, setCancelFor] = useState(null);
  const [cancelReason, setCancelReason] = useState("");
  const payMut = useMutation({
    mutationFn: async (id) => {
      const { data } = await api.post("/payments/checkout", {
        booking_id: id,
        origin_url: window.location.origin,
      });
      return data;
    },
    onSuccess: (data) => { if (data.url) window.location.href = data.url; },
    onError: (e) => toast.error(e.response?.data?.detail || "Could not start payment"),
  });
  const [rescheduleFor, setRescheduleFor] = useState(null);
  const [rNewDate, setRNewDate] = useState("");
  const [rNewSlot, setRNewSlot] = useState("");
  const rescheduleMut = useMutation({
    mutationFn: () => api.post(`/customer/bookings/${rescheduleFor.id}/reschedule`, { date: rNewDate, slot: rNewSlot }),
    onSuccess: () => {
      toast.success("Reschedule requested — trainer will confirm");
      setRescheduleFor(null); setRNewDate(""); setRNewSlot("");
      qc.invalidateQueries({ queryKey: ["cust-bookings"] });
    },
    onError: (e) => toast.error(e.response?.data?.detail || "Could not reschedule"),
  });
  const reviewMut = useMutation({
    mutationFn: () => api.post("/reviews", { booking_id: reviewFor.id, rating, comment }),
    onSuccess: () => {
      toast.success("Review submitted, thanks!");
      setReviewFor(null); setComment(""); setRating(5);
      qc.invalidateQueries({ queryKey: ["cust-bookings"] });
    },
    onError: (e) => toast.error(e.response?.data?.detail || "Could not submit review"),
  });

  return (
    <div className="container-fl py-10">
      <div className="mb-8">
        <div className="overline mb-2">My account</div>
        <h1 className="text-3xl sm:text-4xl font-black tracking-tighter">Customer dashboard</h1>
      </div>

      <div className="flex gap-2 mb-8">
        <button data-testid="tab-bookings" onClick={() => setParams({ tab: "bookings" })} className={`pill ${tab === "bookings" ? "pill-active" : ""}`}><CalendarDays size={14}/> Bookings</button>
        <button data-testid="tab-favourites" onClick={() => setParams({ tab: "favourites" })} className={`pill ${tab === "favourites" ? "pill-active" : ""}`}><Heart size={14}/> Favourites</button>
      </div>

      {tab === "bookings" && (
        <div data-testid="bookings-list" className="space-y-3">
          {bookings.isLoading ? <div className="text-neutral-500">Loading…</div> :
            (bookings.data || []).length === 0 ? (
              <div className="border border-dashed border-neutral-200 rounded-xl p-10 text-center text-neutral-500">
                No bookings yet. <Link to="/trainers" className="text-emerald-700 font-semibold">Browse trainers →</Link>
              </div>
            ) : (bookings.data || []).map((b) => (
              <div key={b.id} className="card-fl p-5 flex items-center justify-between gap-4">
                <div>
                  <div className="text-xs text-neutral-500">{b.date} · {b.slot}</div>
                  <div className="font-bold">{b.service_title}</div>
                  <div className="text-sm text-neutral-500">with {b.trainer_name} · €{b.price}</div>
                </div>
                <div className="flex items-center gap-3">
                  <span className={`text-xs font-bold uppercase tracking-widest rounded-full px-2.5 py-1 ${STATUS_COLOR[b.status] || "bg-neutral-100"}`}>{b.status.replace("_", " ")}</span>
                  {b.paid && (
                    <span className="text-xs font-bold uppercase tracking-widest rounded-full px-2.5 py-1 bg-emerald-600 text-white">Paid</span>
                  )}
                  {b.status === "confirmed" && !b.paid && b.price > 0 && (
                    <button data-testid={`pay-${b.id}`} onClick={() => payMut.mutate(b.id)} disabled={payMut.isPending} className="btn-accent !py-1.5 !px-3 !text-xs">
                      <CreditCard size={12}/> Pay €{b.price}
                    </button>
                  )}
                  {(b.status === "pending" || b.status === "confirmed") && (
                    <button data-testid={`reschedule-${b.id}`} onClick={() => { setRescheduleFor(b); setRNewDate(b.date); setRNewSlot(b.slot); }} className="btn-secondary !py-1.5 !px-3 !text-xs">
                      <CalendarClock size={12}/> Reschedule
                    </button>
                  )}
                  {b.status === "pending" && (
                    <button data-testid={`cancel-${b.id}`} onClick={() => setCancelFor(b)} className="btn-secondary !py-1.5 !px-3 !text-xs"><X size={12}/> Cancel</button>
                  )}
                  {b.status === "completed" && !b.reviewed && (
                    <button data-testid={`review-${b.id}`} onClick={() => setReviewFor(b)} className="btn-accent !py-1.5 !px-3 !text-xs"><Star size={12}/> Review</button>
                  )}
                </div>
              </div>
            ))
          }
        </div>
      )}

      {tab === "favourites" && (
        <div>
          {(favs.data || []).length === 0 ? (
            <div className="border border-dashed border-neutral-200 rounded-xl p-10 text-center text-neutral-500">No favourites yet.</div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {favs.data.map((t) => (
                <div key={t.id} className="relative">
                  <TrainerCard t={t}/>
                  <a href={`/trainer/${t.slug || t.id}#book`} data-testid={`quick-book-${t.id}`}
                     className="absolute top-3 right-3 btn-accent !py-1.5 !px-3 !text-xs shadow-lg">
                    Quick book
                  </a>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      <Dialog open={!!cancelFor} onOpenChange={(v) => !v && (setCancelFor(null), setCancelReason(""))}>
        <DialogContent>
          <DialogHeader><DialogTitle>Cancel booking with {cancelFor?.trainer_name}?</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <p className="text-sm text-neutral-600">
              {cancelFor?.service_title} on {cancelFor?.date} at {cancelFor?.slot}.
            </p>
            <div>
              <label className="text-xs font-bold uppercase tracking-widest text-neutral-500">Reason (optional)</label>
              <select data-testid="cancel-reason-preset" value={cancelReason} onChange={(e) => setCancelReason(e.target.value)}
                      className="mt-1 h-11 w-full rounded-md border border-neutral-200 px-3 text-sm outline-none focus:border-neutral-900">
                <option value="">Prefer not to say</option>
                <option value="Scheduling conflict">Scheduling conflict</option>
                <option value="Found a different trainer">Found a different trainer</option>
                <option value="Price concerns">Price concerns</option>
                <option value="Illness / injury">Illness / injury</option>
                <option value="Changed my mind">Changed my mind</option>
                <option value="Other">Other</option>
              </select>
            </div>
            {cancelReason === "Other" && (
              <textarea data-testid="cancel-reason-custom" rows={2}
                        placeholder="Tell us a bit more..."
                        onChange={(e) => setCancelReason(e.target.value)}
                        className="w-full rounded-md border border-neutral-200 p-3 text-sm outline-none focus:border-neutral-900"/>
            )}
            <p className="text-xs text-neutral-500">This helps us improve the platform. Your trainer will not see this reason.</p>
          </div>
          <DialogFooter>
            <button onClick={() => (setCancelFor(null), setCancelReason(""))} className="btn-secondary">Keep booking</button>
            <button data-testid="confirm-cancel" onClick={() => cancelMut.mutate({ id: cancelFor.id, reason: cancelReason })}
                    className="btn-primary">Cancel booking</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!rescheduleFor} onOpenChange={(v) => !v && setRescheduleFor(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Reschedule {rescheduleFor?.service_title}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <label className="text-xs font-bold uppercase tracking-widest text-neutral-500">New date</label>
              <input data-testid="reschedule-date" type="date" value={rNewDate} onChange={(e) => setRNewDate(e.target.value)}
                     className="mt-1 h-11 w-full rounded-md border border-neutral-200 px-3 text-sm outline-none focus:border-neutral-900"/>
            </div>
            <div>
              <label className="text-xs font-bold uppercase tracking-widest text-neutral-500">New time (HH:MM)</label>
              <input data-testid="reschedule-slot" placeholder="e.g. 10:00" value={rNewSlot} onChange={(e) => setRNewSlot(e.target.value)}
                     className="mt-1 h-11 w-full rounded-md border border-neutral-200 px-3 text-sm outline-none focus:border-neutral-900"/>
            </div>
            <p className="text-xs text-neutral-500">Your trainer will need to accept the new time before it's confirmed.</p>
          </div>
          <DialogFooter>
            <button data-testid="submit-reschedule" disabled={!rNewDate || !rNewSlot || rescheduleMut.isPending}
                    onClick={() => rescheduleMut.mutate()} className="btn-accent w-full">
              {rescheduleMut.isPending ? "Requesting…" : "Request reschedule"}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!reviewFor} onOpenChange={(v) => !v && setReviewFor(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Rate {reviewFor?.trainer_name}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="flex gap-1">
              {[1,2,3,4,5].map((n) => (
                <button key={n} data-testid={`review-star-${n}`} onClick={() => setRating(n)}>
                  <Star size={26} className={n <= rating ? "star-on fill-emerald-600" : "star-off"}/>
                </button>
              ))}
            </div>
            <textarea data-testid="review-comment" value={comment} onChange={(e) => setComment(e.target.value)} rows={4}
                      placeholder="How was your session?" className="w-full rounded-md border border-neutral-200 p-3 text-sm outline-none focus:border-neutral-900"/>
          </div>
          <DialogFooter>
            <button data-testid="review-submit" onClick={() => reviewMut.mutate()} className="btn-accent w-full">Submit review</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
